import { ApolloClient, OnSubscriptionDataOptions } from '@apollo/client';
import { BaseEventPayloadFragment } from '@generated/subscriptionFragments/baseEvent';
import { CarrierMatchEventPayloadFragment } from '@generated/subscriptionFragments/carrierMatchEvent';
import { ClientExceptionRecordEventPayloadFragment } from '@generated/subscriptionFragments/clientExceptionRecordEvent';
import { CommissionsRecalculatedEventPayloadFragment } from '@generated/subscriptionFragments/commissionsRecalculatedEvent';
import { CostLineItemEventPayloadFragment } from '@generated/subscriptionFragments/costLineItemEvent';
import { DriverAssignmentEventPayloadFragment } from '@generated/subscriptionFragments/driverAssignmentEventPayload';
import { IncidentEventPayloadFragment } from '@generated/subscriptionFragments/incidentsEvent';
import { LinkedRouteEventPayloadFragment } from '@generated/subscriptionFragments/linkedRouteEvent';
import { LoadStatusEventPayloadFragment } from '@generated/subscriptionFragments/loadStatusEvent';
import { MessagingNotificationStatusEventPayloadFragment } from '@generated/subscriptionFragments/messagingNotificationStatusEvent';
import { MinionRefreshEventPayloadFragment } from '@generated/subscriptionFragments/minionRefreshEvent';
import { OfferEventPayloadFragment } from '@generated/subscriptionFragments/offerEvent';
import { PricingRateEventPayloadFragment } from '@generated/subscriptionFragments/pricingRateEvent';
import { RateLineItemEventPayloadFragment } from '@generated/subscriptionFragments/rateLineItemEvent';
import { RouteLockingEventPayloadFragment } from '@generated/subscriptionFragments/routeLockingEvent';
import { RouteMaxCostEventPayloadFragment } from '@generated/subscriptionFragments/routeMaxCostEvent';
import { RouteStatusEventPayloadFragment } from '@generated/subscriptionFragments/routeStatusEvent';
import { RouteVendorEventPayloadFragment } from '@generated/subscriptionFragments/routeVendorEvent';
import { SubStopExecutionEventPayloadFragment } from '@generated/subscriptionFragments/subStopExecutionEventPayload';
import { TenderPlanEventPayloadFragment } from '@generated/subscriptionFragments/tenderPlanEvent';
import { TenderPlanRouteEventPayloadFragment } from '@generated/subscriptionFragments/tenderPlanRouteEvent';
import { TrackingStatusEventPayloadFragment } from '@generated/subscriptionFragments/trackingStatusEvent';
import { TrackingUpdateEventPayloadFragment } from '@generated/subscriptionFragments/trackingUpdateEvent';
import { UserMatchEventPayloadFragment } from '@generated/subscriptionFragments/userMatchEvent';
import {
  SubscribeSubscription,
  useSubscribeSubscription,
} from '@generated/subscriptions/subscribe';
import { EventType, ToastEvent } from '@generated/subscriptionTypes';

// To add new EventTypes to the Hook add the new EventType to the EventHandlers
// interface
// and new typename to EventType mapping to the typeNameToEventType function.

type Sig<T extends unknown> = (event: T, client: ApolloClient<object>) => void;

interface EventHandlers {
  [EventType.LinkedRouteEvent]?: Sig<LinkedRouteEventPayloadFragment>;
  [EventType.LoadStatusEvent]?: Sig<LoadStatusEventPayloadFragment>;
  [EventType.RouteStatusEvent]?: Sig<RouteStatusEventPayloadFragment>;
  [EventType.RouteLockingEvent]?: Sig<RouteLockingEventPayloadFragment>;
  [EventType.Unknown]?: Sig<BaseEventPayloadFragment>;
  [EventType.TrackingStatusEvent]?: Sig<TrackingStatusEventPayloadFragment>;
  [EventType.DriverAssignmentEvent]?: Sig<DriverAssignmentEventPayloadFragment>;
  [EventType.RouteVendorEvent]?: Sig<RouteVendorEventPayloadFragment>;
  [EventType.CommissionsRecalculatedEvent]?: Sig<CommissionsRecalculatedEventPayloadFragment>;
  [EventType.CarrierMatchEvent]?: Sig<CarrierMatchEventPayloadFragment>;
  [EventType.UserMatchEvent]?: Sig<UserMatchEventPayloadFragment>;
  [EventType.SubStopExecutionEvent]?: Sig<SubStopExecutionEventPayloadFragment>;
  [EventType.TenderPlanEvent]?: Sig<TenderPlanEventPayloadFragment>;
  [EventType.TenderPlanRouteEvent]?: Sig<TenderPlanRouteEventPayloadFragment>;
  [EventType.TrackingUpdateEvent]?: Sig<TrackingUpdateEventPayloadFragment>;
  [EventType.ToastEvent]?: Sig<ToastEvent>;
  [EventType.OfferEvent]?: Sig<OfferEventPayloadFragment>;
  [EventType.RouteMaxCostEvent]?: Sig<RouteMaxCostEventPayloadFragment>;
  [EventType.PricingRateEvent]?: Sig<PricingRateEventPayloadFragment>;
  [EventType.CostLineItemEvent]?: Sig<CostLineItemEventPayloadFragment>;
  [EventType.RateLineItemEvent]?: Sig<RateLineItemEventPayloadFragment>;
  [EventType.ClientExceptionRecordEvent]?: Sig<ClientExceptionRecordEventPayloadFragment>;
  [EventType.IncidentEvent]?: Sig<IncidentEventPayloadFragment>;
  [EventType.MinionRefreshEvent]?: Sig<MinionRefreshEventPayloadFragment>;
  [EventType.MessagingNotificationStatusEvent]?: Sig<MessagingNotificationStatusEventPayloadFragment>;
}

const typeNameToEventType: (
  typename: SubscribeSubscription['subscribe']['__typename']
) => keyof EventHandlers = (typename) => {
  switch (typename) {
    case 'DriverAssignmentEvent':
      return EventType.DriverAssignmentEvent;
    case 'LinkedRouteEvent':
      return EventType.LinkedRouteEvent;
    case 'LoadStatusEvent':
      return EventType.LoadStatusEvent;
    case 'RouteStatusEvent':
      return EventType.RouteStatusEvent;
    case 'RouteLockingEvent':
      return EventType.RouteLockingEvent;
    case 'TrackingStatusEvent':
      return EventType.TrackingStatusEvent;
    case 'RouteVendorEvent':
      return EventType.RouteVendorEvent;
    case 'CommissionsRecalculatedEvent':
      return EventType.CommissionsRecalculatedEvent;
    case 'CarrierMatchEvent':
      return EventType.CarrierMatchEvent;
    case 'UserMatchEvent':
      return EventType.UserMatchEvent;
    case 'SubStopExecutionEvent':
      return EventType.SubStopExecutionEvent;
    case 'TenderPlanEvent':
      return EventType.TenderPlanEvent;
    case 'TenderPlanRouteEvent':
      return EventType.TenderPlanRouteEvent;
    case 'TrackingUpdateEvent':
      return EventType.TrackingUpdateEvent;
    case 'ToastEvent':
      return EventType.ToastEvent;
    case 'OfferEvent':
      return EventType.OfferEvent;
    case 'RouteMaxCostEvent':
      return EventType.RouteMaxCostEvent;
    case 'PricingRateEvent':
      return EventType.PricingRateEvent;
    case 'CostLineItemEvent':
      return EventType.CostLineItemEvent;
    case 'RateLineItemEvent':
      return EventType.RateLineItemEvent;
    case 'ClientExceptionRecordEvent':
      return EventType.ClientExceptionRecordEvent;
    case 'IncidentEvent':
      return EventType.IncidentEvent;
    case 'MinionRefreshEvent':
      return EventType.MinionRefreshEvent;
    case 'MessagingNotificationStatusEvent':
      return EventType.MessagingNotificationStatusEvent;
    case 'BaseEvent':
    default:
      return EventType.Unknown;
  }
};

interface SubscriptionHookOptions {
  eventHandlers: EventHandlers;
  subscriptionIds?: string[];
  disable?: boolean;
}

/**
 * Hook to reduce duplicate code and simplify interacting with the subscription
 * service
 *
 * Example Usage:
 *
 * useSubscriptionService({
 *   disable: !useSubscription,
 *   subscriptionIds,
 *   eventHandlers: {
 *     [EventType.RouteLockingEvent]: (
 *       event: RouteLockingEventPayloadFragment
 *     ): void => {
 *       doRouteLockingEventStuff();
 *     },
 *     [EventType.LoadStatusEvent]: (
 *       event: LoadStatusEventPayloadFragment
 *     ): void => {
 *       doLoadStatusEventStuff()
 *     },
 *   },
 * });
 *
 * @param options The `eventHandler` object is used to determine which events
 *   to subscribe to and how to handle their events. Pass in the
 *   `subscriptionIds` to filter subscription events based on Ids (Not passing
 *   this will subscribe to all events)
 */
export const useSubscriptionService: (
  options: SubscriptionHookOptions
) => void = ({ eventHandlers, subscriptionIds, disable }) => {
  const onSubscriptionData = ({
    subscriptionData: { data },
    client,
  }: OnSubscriptionDataOptions<SubscribeSubscription>): void => {
    if (!data) {
      return;
    }

    const eventType = typeNameToEventType(data.subscribe.__typename);
    // TODO: Currently doing `as fixMe` to simplify addition of EventTypes in the future.
    // This could be a switch on the data.subscribe.__typename to enable type-guarding
    eventHandlers[eventType]?.(data.subscribe as fixMe, client);
  };

  // Pull EventTypes to subscribe to from EventHandler
  const eventTypes: EventType[] = Object.keys(eventHandlers) as EventType[];

  useSubscribeSubscription({
    variables: {
      ids: subscriptionIds,
      eventTypes,
    },
    onSubscriptionData: onSubscriptionData,
    // only enable subscription when there is data we want to fetch
    skip: disable || subscriptionIds?.length === 0,
  });
};
